import { z } from 'zod';
import { entity, IdSchema, IsoDateTimeSchema } from './common';

// ---------- Counseling Journal (Amendment A3) — 상담기록지의 유일한 승인 단위 ----------

export const JOURNAL_FORM_FIELDS = [
  'counselingReasonAndGoal',
  'assessment',
  'pastCounselingExperience',
  'importantIssue',
  'previousSessionIssueOrTask',
  'previousTaskCompletion',
  'counselingContent',
  'nextSessionPlan',
  'remarks',
] as const;

export const FieldSourceSchema = z.enum(['auto', 'ai-draft', 'user']);

export const CounselingJournalSchema = entity('CounselingJournal', {
  clientId: IdSchema,
  sessionId: IdSchema,

  counselingReasonAndGoal: z.string().optional(),
  assessment: z.string().optional(),
  pastCounselingExperience: z.string().optional(),
  importantIssue: z.string().optional(),
  previousSessionIssueOrTask: z.string().optional(),
  previousTaskCompletion: z.string().optional(),
  counselingContent: z.string().optional(),
  nextSessionPlan: z.string().optional(),
  remarks: z.string().optional(),

  sessionTopic: z.string().optional(),
  presentingIssueThisSession: z.string().optional(),
  focus: z.string().optional(),
  majorEvents: z.string().optional(),
  importantClientReports: z.string().optional(),
  emotionalSummary: z.string().optional(),
  cognitiveSummary: z.string().optional(),
  interventionsUsed: z.string().optional(),
  counselorApproach: z.string().optional(),
  clientResponse: z.string().optional(),
  engagement: z.string().optional(),
  resistance: z.string().optional(),
  counselorAssessment: z.string().optional(),
  progressAssessment: z.string().optional(),
  riskSummary: z.string().optional(),
  goalsUpdated: z.string().optional(),

  internalCounselorMemo: z.string().optional(),

  fieldSources: z.record(z.string(), FieldSourceSchema).optional(),

  status: z.enum(['draft', 'reviewed', 'approved']),
  approvedAt: IsoDateTimeSchema.optional(),
  previousVersionId: IdSchema.optional(),
  supersededById: IdSchema.nullable().optional(),
});
export type CounselingJournal = z.infer<typeof CounselingJournalSchema>;

// ---------- Next Session Brief ----------

export const PlannedQuestionSchema = z
  .object({
    id: IdSchema,
    question: z.string().min(1),
    purpose: z.string().optional(),
    relatedHypothesis: z.string().optional(),
    caution: z.string().optional(),
  })
  .strict();

export type PlannedQuestion = z.infer<typeof PlannedQuestionSchema>;

export const PlannedInterventionSchema = z
  .object({
    id: IdSchema,
    intervention: z.string().min(1),
    orientation: z.string().optional(),
    rationale: z.string().optional(),
    expectedGoal: z.string().optional(),
    caution: z.string().optional(),
    suitability: z.enum(['low', 'medium', 'high']).optional(),
  })
  .strict();

export const NextSessionBriefSchema = entity('NextSessionBrief', {
  clientId: IdSchema,
  sourceSessionId: IdSchema,
  targetSessionNumber: z.number().int().min(1),

  previousSessionSummary: z.string().optional(),
  activeGoalIds: z.array(IdSchema).optional(),
  recentChanges: z.array(z.string()).optional(),
  formulationSummary: z.string().optional(),
  hypothesesToTest: z.array(z.string()).optional(),
  sessionObjectives: z.array(z.string()).optional(),
  assessmentQuestions: z.array(PlannedQuestionSchema).optional(),
  interventionSuggestions: z.array(PlannedInterventionSchema).optional(),
  riskFollowUp: z.array(z.string()).optional(),
  unfinishedTopics: z.array(z.string()).optional(),

  generatedBy: z.enum(['rule', 'ai', 'user']),
  status: z.enum(['draft', 'approved', 'used']),
  usedAt: IsoDateTimeSchema.optional(),
});
export type NextSessionBrief = z.infer<typeof NextSessionBriefSchema>;

// ---------- Supervision / Reflection / Assessment / Verbatim ----------

export const SupervisionNoteSchema = entity('SupervisionNote', {
  clientId: IdSchema,
  sessionId: IdSchema.optional(),
  supervisionQuestion: z.string().optional(),
  counselorDifficulty: z.string().optional(),
  feedbackReceived: z.string().optional(),
  alternativeIntervention: z.string().optional(),
  learningPoint: z.string().optional(),
  actionForNextSession: z.string().optional(),
  source: z.enum(['self-review', 'claude-review', 'human-supervision']),
});
export type SupervisionNote = z.infer<typeof SupervisionNoteSchema>;

export const CounselorReflectionSchema = entity('CounselorReflection', {
  clientId: IdSchema,
  sessionId: IdSchema.optional(),
  kind: z.enum(['self-understanding', 'theological-reflection']),
  text: z.string(),
});
export type CounselorReflection = z.infer<typeof CounselorReflectionSchema>;

/** Amendment A35: 검사 점수 한 줄 (주 척도·하위척도) */
export const AssessmentScoreSchema = z
  .object({
    key: z.string().min(1).max(20),
    label: z.string().max(80),
    level: z.enum(['scale', 'subscale']),
    raw: z.number().optional(),
    t: z.number().optional(),
    percentile: z.number().optional(),
    /** 규준집단 평균·표준편차 (하위척도) */
    mean: z.number().optional(),
    sd: z.number().optional(),
  })
  .strict();
export type AssessmentScore = z.infer<typeof AssessmentScoreSchema>;

/** Amendment A42: 문항별 응답 (문장완성검사 등). stem = 문항(앞부분), response = 내담자가 쓴 응답 */
export const AssessmentItemSchema = z
  .object({
    no: z.number().int().min(1).max(500),
    stem: z.string().max(500),
    response: z.string().max(3000),
    /** 손글씨가 확실하지 않아 확인이 필요한 응답 */
    unclear: z.boolean().optional(),
  })
  .strict();
export type AssessmentItem = z.infer<typeof AssessmentItemSchema>;

export const PsychologicalAssessmentSchema = entity('PsychologicalAssessment', {
  clientId: IdSchema,
  sessionId: IdSchema.optional(),
  testName: z.string().min(1),
  administeredAt: z.string().optional(),
  scoresSummary: z.string().optional(),
  interpretation: z.string().optional(),
  source: z.enum(['counselor', 'external-record']),
  /** Amendment A35: 검사 종류, 규준집단, 무응답 수, 점수 표 (결과지에서 읽은 것) */
  testKind: z.enum(['tci', 'mmpi2', 'sct', 'other']).optional(),
  normGroup: z.string().max(40).optional(),
  missingResponses: z.number().int().min(0).optional(),
  scores: z.array(AssessmentScoreSchema).max(200).optional(),
  /** Amendment A35: 해석 출처와 상담자 확인 */
  interpretationSource: z.enum(['counselor', 'external-ai']).optional(),
  interpretationApprovedAt: IsoDateTimeSchema.optional(),
  /** Amendment A42: 문항별 응답과 그 출처 (외부 AI가 검사지에서 옮김 / 상담자 입력), 상담자가 응답을 확인한 때 */
  items: z.array(AssessmentItemSchema).max(500).optional(),
  itemsSource: z.enum(['external-ai', 'counselor']).optional(),
  itemsCheckedAt: IsoDateTimeSchema.optional(),
});
export type PsychologicalAssessment = z.infer<typeof PsychologicalAssessmentSchema>;

export const VerbatimSelectionSchema = entity('VerbatimSelection', {
  clientId: IdSchema,
  sessionId: IdSchema,
  startMs: z.number().int().min(0),
  endMs: z.number().int().min(0),
  segmentIdsSnapshot: z.array(z.string()),
  selectionReason: z.string().optional(),
  processNotes: z.string().optional(),
  supervisionQuestions: z.array(z.string()).optional(),
  proposedBy: z.enum(['user', 'ai']),
  status: z.enum(['candidate', 'selected']),
});
export type VerbatimSelection = z.infer<typeof VerbatimSelectionSchema>;

// ---------- Documents ----------

export const DocumentArtifactSchema = entity('DocumentArtifact', {
  clientId: IdSchema,
  sessionId: IdSchema.optional(),
  /** Amendment A24: verbatim (축어록 문서) 추가 */
  documentType: z.enum(['session_record', 'case_report', 'termination_report', 'verbatim', 'dialogue']),
  /** Amendment A24: 축어록 문서의 원본 선택 구간 */
  verbatimSelectionId: IdSchema.optional(),
  status: z.enum(['draft', 'reviewed', 'approved']),
  includedSessionIds: z.array(IdSchema).optional(),
  sourceFormulationRevision: z.number().int().min(1).optional(),
  /** 생성 시점 스냅샷 (TEST_PLAN §9) */
  asOf: IsoDateTimeSchema,
  content: z.string(),
  fieldSources: z.record(z.string(), z.enum(['auto', 'needs-review', 'missing', 'user'])).optional(),
  approvedAt: IsoDateTimeSchema.optional(),
  previousVersionId: IdSchema.optional(),
});
export type DocumentArtifact = z.infer<typeof DocumentArtifactSchema>;
